import { WEEK_VOCABULARY, type WeekDay, type WeekExercise, type WeekLevel, type WeekSet, type WeekTree } from "./reviewerWeekVocabulary.generated";

/* THE REVIEWER'S WEEK — reader, checker and the COPY's edits (reviewer seat step 2, 2026-10-10; brief § Week 8b).
 *
 * William reads the client's written week through `reviewer_week(client, monday, monday + 6)` and edits a COPY of it in
 * memory; the save is ONE row of `reviewer_week_proposals` (`week` = the edited tree). Nothing here, and nothing that
 * imports this, writes a `coach_*` row — the proposal is data the client and the Saturday week writer READ.
 *
 * ⚠️ ONE VOCABULARY, NEVER RETYPED: every level's keys and kinds are `WEEK_VOCABULARY`, generated from the migration's
 * `reviewer_week_vocabulary()` literal (`tools/reviewer-week-vocab/generate.py`; the drift ruler is
 * `reviewerWeekVocabulary.test.ts`). `nodeOk` walks THAT object — the same walk as the SQL CHECK
 * (`reviewer_week_node_ok`), so a tree this page would save is a tree the database accepts, checked BEFORE the write. */

/** The SQL check's caps (`reviewer_week_node_ok` / `reviewer_week_shape_ok`, measured live 2026-10-10). */
export const WEEK_CAPS = { section: 30, exercise: 60, set: 40, days: 35, bytes: 524288 } as const;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** One node: EXACTLY the level's keys, each of its kind; children recursed (the SQL `reviewer_week_node_ok`). */
export function nodeOk(node: unknown, level: WeekLevel): boolean {
  const spec = WEEK_VOCABULARY[level] as Record<string, string>;
  if (node === null || typeof node !== "object" || Array.isArray(node)) return false;
  const o = node as Record<string, unknown>;
  const keys = Object.keys(o).sort();
  const want = Object.keys(spec).sort();
  if (keys.length !== want.length || keys.some((k, i) => k !== want[i])) return false;
  for (const [k, kind] of Object.entries(spec)) {
    const v = o[k];
    if (kind.startsWith("[]")) {
      const child = kind.slice(2) as WeekLevel;
      const cap = (WEEK_CAPS as Record<string, number>)[child];
      if (!Array.isArray(v) || cap === undefined || v.length > cap) return false;
      if (!v.every((c) => nodeOk(c, child))) return false;
    } else if (kind.startsWith("@")) {
      if (!nodeOk(v, kind.slice(1) as WeekLevel)) return false;
    } else if (v === null) {
      if (kind === "date" || kind === "name") return false;
    } else if (kind === "uuid") {
      if (typeof v !== "string" || !UUID.test(v)) return false;
    } else if (kind === "date") {
      if (typeof v !== "string" || !DATE.test(v)) return false;
    } else if (kind === "int" || kind === "num") {
      if (typeof v !== "number" || !Number.isFinite(v) || Math.abs(v) > 100000 || (kind === "int" && !Number.isInteger(v))) return false;
    } else if (kind === "bool") {
      if (typeof v !== "boolean") return false;
    } else if (kind === "text") {
      if (typeof v !== "string" || [...v].length > 4000) return false;
    } else if (kind === "name") {
      if (typeof v !== "string" || [...v.trim()].length < 1 || [...v.trim()].length > 200) return false;
    } else if (kind === "object") {
      if (typeof v !== "object" || Array.isArray(v) || new TextEncoder().encode(JSON.stringify(v)).length > 4000) return false;
    } else if (kind === "texts") {
      if (!Array.isArray(v) || v.length > 20 || v.some((s) => typeof s !== "string" || [...s].length > 80)) return false;
    } else {
      return false; // a kind the vocabulary names and this check does not know — refused, as the SQL refuses it
    }
  }
  return true;
}

/** The whole week (the SQL `reviewer_week_shape_ok`): ≤ 35 days, ≤ 512 KB, every day inside [monday, monday + 6]. */
export function weekOk(week: unknown, monday: string): boolean {
  if (!Array.isArray(week) || week.length > WEEK_CAPS.days) return false;
  if (new TextEncoder().encode(JSON.stringify(week)).length > WEEK_CAPS.bytes) return false;
  const end = addDays(monday, 6);
  return week.every((d) => nodeOk(d, "day") && (d as WeekDay).scheduled_date >= monday && (d as WeekDay).scheduled_date <= end);
}

/** Reads `reviewer_week`'s jsonb as a tree, or null when it does not match the vocabulary (a failed read, never a guess). */
export function readWeek(raw: unknown, monday: string): WeekTree | null {
  return weekOk(raw, monday) ? (raw as WeekTree) : null;
}

/* ── dates — plain calendar days, never a clock ─────────────────────────────────────────────────────────────────── */

export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}
/** The Monday of the week holding `iso`. */
export function mondayOf(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return addDays(iso, -((dow + 6) % 7));
}
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export function weekdayOf(iso: string, monday: string): string {
  const i = Math.round((Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${monday}T00:00:00Z`)) / 86400000);
  return WEEKDAYS[i] ?? iso;
}

/* ── the COPY's edits — pure, each returns a new tree; an added node carries null ids ───────────────────────────── */

export type ExercisePath = { day: number; section: number; exercise: number };

const sortDays = (w: WeekTree): WeekTree =>
  [...w].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || (a.day_position ?? 99) - (b.day_position ?? 99));

const mapExercise = (w: WeekTree, p: ExercisePath, f: (e: WeekExercise) => WeekExercise | null): WeekTree =>
  w.map((d, di) => di !== p.day ? d : {
    ...d,
    coach_workouts: {
      ...d.coach_workouts,
      coach_workout_sections: d.coach_workouts.coach_workout_sections.map((s, si) => si !== p.section ? s : {
        ...s,
        coach_workout_exercises: renumber(s.coach_workout_exercises.flatMap((e, ei) => {
          if (ei !== p.exercise) return [e];
          const n = f(e);
          return n === null ? [] : [n];
        })),
      }),
    },
  });

const renumber = <T extends { position: number | null }>(xs: T[]): T[] => xs.map((x, i) => ({ ...x, position: i }));

export function removeDay(w: WeekTree, day: number): WeekTree {
  return w.filter((_, i) => i !== day);
}
/** Moves a day to another date of the same week (a new split); its rows keep their ids — the same workout, moved. */
export function moveDay(w: WeekTree, day: number, date: string): WeekTree {
  return sortDays(w.map((d, i) => (i === day ? { ...d, scheduled_date: date } : d)));
}
/** A new, empty day: one unnamed section to add exercises into. */
export function addDay(w: WeekTree, date: string, name: string): WeekTree {
  const day: WeekDay = {
    id: null, scheduled_date: date, day_position: null, is_deload: false, status: null, note: null,
    coach_workouts: {
      id: null, name: name.trim() === "" ? "New day" : name.trim(), description: null, kind: null,
      coach_workout_sections: [{ position: 0, name: null, section_type: null, format: null, format_config: null, description: null, coach_workout_exercises: [] }],
    },
  };
  return sortDays([...w, day]);
}
export function renameDay(w: WeekTree, day: number, name: string): WeekTree {
  return w.map((d, i) => (i === day ? { ...d, coach_workouts: { ...d.coach_workouts, name } } : d));
}
export function removeExercise(w: WeekTree, p: ExercisePath): WeekTree {
  return mapExercise(w, p, () => null);
}
/** A movement from the reviewer's own library — what an added or swapped row is. */
export type PickedMovement = { exercise_id: string | null; name: string; tracking_fields: string[] | null };

/** Swapped: a DIFFERENT exercise in the same place, the written sets kept; the row's id goes (identity = exercise + day). */
export function swapExercise(w: WeekTree, p: ExercisePath, m: PickedMovement): WeekTree {
  return mapExercise(w, p, (e) => ({ ...e, id: null, exercise_id: m.exercise_id, name: m.name, tracking_fields: m.tracking_fields }));
}
/** Added at the end of a section: three working sets, nothing written in them yet. */
export function addExercise(w: WeekTree, day: number, section: number, m: PickedMovement): WeekTree {
  const blank = (i: number): WeekSet => ({ position: i, set_type: "working", reps: null, weight_kg: null, time_seconds: null, rounds: null, distance_m: null, rest_seconds: null });
  const ex: WeekExercise = {
    id: null, position: 0, exercise_id: m.exercise_id, name: m.name, tracking_fields: m.tracking_fields, sets: 3,
    reps_min: null, reps_max: null, hold_seconds_min: null, hold_seconds_max: null, rest_seconds: null, tempo: null, notes: null,
    each_side: false, superset_with_next: false, hold_until_lost: false, coach_workout_sets: [blank(0), blank(1), blank(2)],
  };
  return w.map((d, di) => di !== day ? d : {
    ...d,
    coach_workouts: {
      ...d.coach_workouts,
      coach_workout_sections: d.coach_workouts.coach_workout_sections.map((s, si) =>
        si !== section ? s : { ...s, coach_workout_exercises: renumber([...s.coach_workout_exercises, ex]) }),
    },
  });
}
/** One set's field; `null` clears it. */
export type SetField = "reps" | "weight_kg" | "time_seconds" | "distance_m" | "rounds";
export function setCell(w: WeekTree, p: ExercisePath, set: number, field: SetField, value: number | null): WeekTree {
  return mapExercise(w, p, (e) => ({
    ...e, coach_workout_sets: e.coach_workout_sets.map((s, i) => (i === set ? { ...s, [field]: value } : s)),
  }));
}
export function addSet(w: WeekTree, p: ExercisePath): WeekTree {
  return mapExercise(w, p, (e) => {
    const last = e.coach_workout_sets[e.coach_workout_sets.length - 1];
    const next: WeekSet = last !== undefined ? { ...last, position: e.coach_workout_sets.length }
      : { position: 0, set_type: "working", reps: null, weight_kg: null, time_seconds: null, rounds: null, distance_m: null, rest_seconds: null };
    const sets = [...e.coach_workout_sets, next];
    return { ...e, sets: sets.length, coach_workout_sets: sets };
  });
}
export function removeSet(w: WeekTree, p: ExercisePath, set: number): WeekTree {
  return mapExercise(w, p, (e) => {
    const sets = renumber(e.coach_workout_sets.filter((_, i) => i !== set));
    return { ...e, sets: sets.length, coach_workout_sets: sets };
  });
}
export function setNotes(w: WeekTree, p: ExercisePath, notes: string): WeekTree {
  return mapExercise(w, p, (e) => ({ ...e, notes: notes.trim() === "" ? null : notes }));
}

/* ── what changed — computed at READ, never stored (brief § Week 8b: matched by exercise identity + day) ─────────── */

const exKey = (e: WeekExercise) => e.exercise_id ?? `name:${e.name.trim().toLowerCase()}`;
const setsKey = (e: WeekExercise) => JSON.stringify(e.coach_workout_sets.map((s) => [s.set_type, s.reps, s.weight_kg, s.time_seconds, s.distance_m, s.rounds]));

/** `movedFrom` / `movedTo`: the same written workout (its id) now on another date — one move, never "removed" + "new". */
export type DayChange = { date: string; added: string[]; removed: string[]; changed: string[]; dayAdded: boolean; dayRemoved: boolean;
  movedFrom: string | null; movedTo: string | null };

/** Per date: which exercises the proposal adds, removes or re-numbers against the written week. */
export function weekChanges(written: WeekTree, proposed: WeekTree): DayChange[] {
  const dates = [...new Set([...written, ...proposed].map((d) => d.scheduled_date))].sort();
  const ids = (w: WeekTree, date: string) => w.filter((d) => d.scheduled_date === date).map((d) => d.coach_workouts.id).filter((x): x is string => x !== null);
  const whereIs = (w: WeekTree, id: string) => w.find((d) => d.coach_workouts.id === id)?.scheduled_date ?? null;
  const flat = (w: WeekTree, date: string) => w.filter((d) => d.scheduled_date === date)
    .flatMap((d) => d.coach_workouts.coach_workout_sections.flatMap((s) => s.coach_workout_exercises));
  return dates.map((date) => {
    const a = flat(written, date);
    const b = flat(proposed, date);
    const left = new Map<string, WeekExercise[]>();
    for (const e of a) left.set(exKey(e), [...(left.get(exKey(e)) ?? []), e]);
    const added: string[] = []; const changed: string[] = [];
    for (const e of b) {
      const q = left.get(exKey(e));
      const m = q?.shift();
      if (m === undefined) added.push(e.name);
      else if (setsKey(m) !== setsKey(e) || (m.notes ?? "") !== (e.notes ?? "")) changed.push(e.name);
    }
    const removed = [...left.values()].flat().map((e) => e.name);
    const dayAdded = !written.some((d) => d.scheduled_date === date);
    const dayRemoved = !proposed.some((d) => d.scheduled_date === date);
    const movedFrom = dayAdded ? (ids(proposed, date).map((id) => whereIs(written, id)).find((x) => x !== null && x !== date) ?? null) : null;
    const movedTo = dayRemoved ? (ids(written, date).map((id) => whereIs(proposed, id)).find((x) => x !== null && x !== date) ?? null) : null;
    // A moved day's exercises are compared against where it came from, so a move alone names nothing else.
    if (movedFrom !== null) return { date, added: [], removed: [], changed: [], dayAdded, dayRemoved, movedFrom, movedTo };
    return { date, added, removed, changed, dayAdded, dayRemoved, movedFrom, movedTo };
  }).filter((c) => c.added.length + c.removed.length + c.changed.length > 0 || c.dayAdded || c.dayRemoved);
}

/** How many dates the proposal differs on — the page's one-line summary. */
export function changeCount(written: WeekTree, proposed: WeekTree): number {
  return weekChanges(written, proposed).length;
}
